
import React, { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import AccountHeader from "@/components/account/AccountHeader";
import AccountTabs from "@/components/account/AccountTabs";
import AccountLoading from "@/components/account/AccountLoading";
import SubscriptionArea from "@/components/account/SubscriptionArea";
import ReferralSection from "@/components/account/ReferralSection";
import FollowedStrategiesLoader from "@/components/account/FollowedStrategiesLoader";
import { MarketplaceStrategy } from "@/types/strategy-types";
import { useStrategyActions } from "@/components/account/StrategyActions";
import StrategyDetail from "@/components/StrategyDetail";
import { useUserAccount } from "@/hooks/account/useUserAccount";
import { useIsMobile } from "@/hooks/use-mobile";
import DeleteAccountArea from "@/components/account/DeleteAccountArea";
import ChatOption from "@/components/ChatOption";

const Account = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("my-strategies");
  const [followedStrategies, setFollowedStrategies] = useState<MarketplaceStrategy[]>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Use the useUserAccount hook to manage user data and profile image updates
  const {
    userData,
    subscriptionStatus,
    loading,
    updateProfileImage
  } = useUserAccount();

  const { handleSelectStrategy, handleUnfollowStrategy } = useStrategyActions();

  // Handle strategies loaded from the FollowedStrategiesLoader
  const handleStrategiesLoaded = (strategies: MarketplaceStrategy[]) => {
    setFollowedStrategies(strategies);
    setIsLoadingStrategies(false);
  };

  // Reset loading state for strategies when user changes
  useEffect(() => {
    if (userData.userId) {
      setIsLoadingStrategies(true);
    }
  }, [userData.userId]);

  // Function to handle strategy unfollowing with UI update
  const handleUnfollow = async (id: string) => {
    const success = await handleUnfollowStrategy(id);
    if (success) {
      // Remove the unfollowed strategy from the local state
      setFollowedStrategies(prevStrategies =>
        prevStrategies.filter(strategy => strategy.id !== id)
      );
    }
  };

  // Find the selected strategy object for the detail view
  const selectedStrategyObject = followedStrategies.find(
    s => (s.strategy_name || s.name) === selectedStrategy
  );

  // Function to handle strategy card selection
  const onSelectStrategy = (strategyName: string) => {
    setSelectedStrategy(strategyName);
  };

  if (loading) {
    return <AccountLoading />;
  }


  return (
    <div className={`min-h-screen bg-gray-50 ${isMobile ? 'pb-14' : ''}`}>
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8">
        <AccountHeader
          firstName={userData.firstName}
          lastName={userData.lastName}
          email={userData.email}
          profileImageUrl={userData.profileImageUrl}
        />

        {/* Hidden loader component */}
        <div className="hidden">
          <FollowedStrategiesLoader
            userId={userData.userId}
            isUserLoading={loading}
            onStrategiesLoaded={handleStrategiesLoaded}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          {/* Subscription section - moved to the right on desktop */}
          <div className="lg:col-span-4 lg:order-2 space-y-8">
            <SubscriptionArea
              status={subscriptionStatus.status}
              plan={subscriptionStatus.plan}
              nextBillingDate={subscriptionStatus.nextBillingDate}
              subscriptionId={subscriptionStatus.subscriptionId}
              isCancelled={subscriptionStatus.isCancelled}
            />
            <DeleteAccountArea userId={userData.userId} />
          </div>

          {/* Main content section */}
          <div className="lg:col-span-8 lg:order-1 space-y-6">
            <ReferralSection />

            <div className="bg-white rounded-lg shadow">
              <AccountTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                userData={{
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  email: userData.email,
                  profileImageUrl: userData.profileImageUrl
                }}
                updateProfileImage={updateProfileImage}
                followedStrategies={followedStrategies}
                recommendedStrategies={[]} // Empty for now, could be implemented later
                isLoadingStrategies={isLoadingStrategies}
                onSelectStrategy={onSelectStrategy}
                onUnfollowStrategy={handleUnfollow}
              />
            </div>
          </div>
        </div>
      </main>
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}

      {/* Strategy detail dialog */}
      {selectedStrategyObject && (
        <StrategyDetail
          isOpen={!!selectedStrategy}
          onClose={() => setSelectedStrategy(null)}
          strategy={selectedStrategyObject}
        />
      )}

      <Footer />
    </div>
  );
};

export default Account;
